import AddressLayout from '@/components/Address'
import { ButtonWithLoading } from '@/components/buttons'
import IconButton from '@/components/buttons/IconButton'
import { RecommendCompetitions } from '@/components/Competition'
import { Checkbox, ExpandableBlock, LoadingView, TextInput } from '@/components/FormElement'
import { ImgFallback } from '@/components/Image'
import { AppLayout } from '@/components/Layouts/AppLayout'
import TotalLayout from '@/components/Layouts/TotalLayout'
import { ConfirmModal } from '@/components/Modal'
import { OrderAddress, OrderCountDown } from '@/components/Orders'
import ApplePayButton from '@/components/payment/ApplePayButton'
import { GooglePayButton } from '@/components/payment/GooglePayButton'
import PayButtonGroup from '@/components/payment/PayButtonGroup'
import { useUserBalance } from '@/hooks/Balance'
import { useLanguage, useLayout, useMyCart, useStats, useSystemConfig, useUser } from '@/hooks/Contexts'
import { API_URL } from '@/utils/Consts'
import { floatAddition, floatMultiplication, formatWithGBP } from '@/utils/FloatNumber'
import { Format } from '@/utils/Format'
import { svgs } from '@/utils/Images'
import { Action1 } from '@/utils/Types'
import { CartDetail as CartDetailType, CartItem as CartItemType } from '@models/Index'
import { appUtils } from 'lib/AppUtils'
import axios from 'lib/axios'
import moment from 'moment'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ChangeEvent, Dispatch, FC, Fragment, SetStateAction, useEffect, useMemo, useState } from 'react'
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form'
import { toast } from 'react-toastify'
import { KeyedMutator, useSWRConfig } from 'swr'
const CartItemCountDown = dynamic(() => import('../../components/CartItemCountDown'))

export default function Cart() {
  const { errors: errStr, cart: cartStr } = useLanguage()
  const { isDesktop } = useLayout()
  const router = useRouter()
  const { error, msg } = router.query
  const user = useUser()

  const myCart = useMyCart()
  const [cartDetail, setCartDetail] = useState(null as CartDetailType)
  const [loading, setLoading] = useState(true)
  const [hasOrder, setHasOrder] = useState(false)
  const [paying, setPaying] = useState(false)

  const [couponData, setCouponData] = useState<{ code: string; needValid: boolean; valid: boolean; message: string }>({
    code: '',
    needValid: false,
    valid: false,
    message: '',
  })

  const formInstance = useForm({ mode: 'onBlur' })
  const { reset, getValues } = formInstance

  useEffect(() => {
    if (error) {
      const errorMsg = appUtils.isEmpty(msg) ? appUtils.getErrorMsgByCode(error.toString(), errStr) : msg.toString()
      toast.error(errorMsg, { toastId: error.toString() })
      router.replace(router.pathname, null, { shallow: true })
    }
  }, [error])

  useEffect(() => {
    if (error) {
      return
    }
    setLoading(true)
    axios
      .get(API_URL.cartDetail + '?store_id=1')
      .then(res => {
        setLoading(false)
        if (res && res.data && res.data.code == '0') {
          const cartData = res.data.data
          setCartDetail(cartData)
          updateCartLocal(cartData)
          if (typeof window?.['klaviyo']?.push != 'undefined') {
            const event = {
              $event_id: `checkout-${user?.id || 'anonym'}-${moment().unix()}`,
              $value: cartDetail.grand_total,
              CheckoutURL: process.env.NEXT_PUBLIC_FRONTEND_URL + router.asPath,
              Items: cartDetail.items?.map(item => ({
                CompetitionID: item['competition_id'],
                Store: 'UK',
                ProductName: item.title,
                CompetitionTickets: item['tickets_total'],
                CompetitionWinners: 1 + item['has_instant_win'],
                CompetitionDefaultTicketNumber: item['default_ticket_amount'],
                CompetitionDrawDate: moment(item['draw_date']).unix(),
                InstantWinComp: item['has_instant_win'] > 0 ? 'Yes' : 'No',
                InstantWinPrizes: item['has_instant_win'],
                Quantity: item.qty,
                ItemPrice: item['sale_price'] || item.price,
                RowTotal: floatMultiplication(item['sale_price'] || item.price, item.qty),
                ProductURL: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/competitions/${item.short_name}`,
                ImageURL: cartDetail['images']?.[0] || '',
                ProductCategories: item['categories'] || [],
              })),
            }
            window['klaviyo'].push(['track', 'Started Checkout', event])
          }
        }
      })
      .catch(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (cartDetail && !appUtils.isEmpty(cartDetail)) {
      setHasOrder(cartDetail.has_order)
      if (cartDetail.coupon_code) {
        setCouponData({ ...couponData, code: cartDetail.coupon_code, needValid: true, valid: true })
      } else {
        setCouponData({ ...couponData, code: '', needValid: false, message: '' })
      }
      const defaultValues = getValues()
      if (cartDetail.billing_address) {
        reset({ ...defaultValues, address: cartDetail.billing_address, addrCloseDefault: !isDesktop, sms_subscribe: true })
      } else if (user && !defaultValues?.address?.first_name) {
        appUtils
          .fetcher(API_URL.getUserAddresses, true)
          .then(res => {
            if (res && res.length > 0) {
              const firstAddress = res[0]
              if (!firstAddress.email && !appUtils.isFakeEmail(user.email)) {
                firstAddress.email = user.email
              }
              reset({ ...defaultValues, address: firstAddress, addrCloseDefault: !isDesktop, sms_subscribe: true })
            }
          })
          .catch(() => {})
      } // else --> addrCloseDefault will be undefined, address fields will open as default
    }
  }, [cartDetail])

  useEffect(() => {
    if (myCart && !appUtils.isEmpty(cartDetail)) {
      if (!myCart.id) {
        setCartDetail(null)
      } else if (myCart.grant_total != cartDetail.grand_total) {
        router.reload()
      }
    }
  }, [myCart])

  const { mutate } = useSWRConfig()

  const updateCartLocal = cartData => {
    let countData = { id: 0, items: 0, grant_total: 0 }
    if (cartData && !Array.isArray(cartData)) {
      countData = {
        id: cartData.id,
        items: cartData.total_items_amount,
        grant_total: cartData.grand_total,
      }
    }
    mutate(API_URL.cartCount, { ...countData }, false)
  }

  return (
    <>
      <AppLayout title={cartStr.my_basket}>
        <LoadingView show={paying || loading} fixed={true} />

        {cartDetail && !Array.isArray(cartDetail) && cartDetail.items && cartDetail.items.length > 0 ? (
          <CartContent
            loading={loading}
            setLoading={setLoading}
            cartDetail={cartDetail}
            setCartDetail={setCartDetail}
            couponData={couponData}
            setCouponData={setCouponData}
            updateCartLocal={updateCartLocal}
            hasOrder={hasOrder}
            formInstance={formInstance}
            paying={paying}
            setPaying={setPaying}
          />
        ) : (
          !loading &&
          (appUtils.isEmpty(cartDetail) || cartDetail.items?.length == 0) && (
            <div className='flex w-full flex-col items-center'>
              <div className='mt-4 w-full rounded bg-amber-100 p-4'>{cartStr.basket_empty}</div>
              <button type='button' onClick={() => router.push('/competitions')} className='btn-rd-outline btn-hover-amber btn-width mb-24 mt-6 h-10'>
                {cartStr.return_to_shop}
              </button>
            </div>
          )
        )}
      </AppLayout>
      {!loading && (appUtils.isEmpty(cartDetail) || cartDetail.items?.length == 0) ? <RecommendCompetitions extraCls='container-white' /> : null}
    </>
  )
}

interface CartContentProps {
  cartDetail: CartDetailType
  loading: boolean
  setLoading: Dispatch<SetStateAction<boolean>>
}

const CartContent: FC<
  {
    setCartDetail: Dispatch<SetStateAction<CartDetailType>>
    couponData: Record<string, any>
    setCouponData: Dispatch<SetStateAction<Record<string, any>>>
    updateCartLocal: Action1<any>
    formInstance: UseFormReturn<FieldValues, any>
    paying: boolean
    setPaying: Dispatch<SetStateAction<boolean>>
    hasOrder: boolean
  } & CartContentProps
> = ({ loading, setLoading, cartDetail, setCartDetail, couponData, setCouponData, hasOrder, updateCartLocal, formInstance, paying, setPaying }) => {
  const { errors: errStr, cart: cartStr, wallet: walletStr, payment: payStr } = useLanguage()
  const { screen } = useSystemConfig()
  const router = useRouter()
  const user = useUser()
  const { cash_back, payment } = useStats()
  const isBarclays = useMemo<boolean>(() => payment && Number(payment?.barclaycard) == 1, [payment]) // fallback to checkout as default

  const [agree, setAgree] = useState(true)

  const [confirmModal, setConfirmModal] = useState<Record<string, any>>({ show: false, title: '', content: '' })

  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    setValue,
    formState: { errors },
  } = formInstance

  // Needs to revalidate when there is an error
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target
    if (errors[name]) {
      trigger(name)
    }
  }

  const {
    cash: { data: cash, mutate: mutateCash },
    credit: { data: credit, mutate: mutateCredit },
  } = useUserBalance(user)

  const onSubmit = data => {
    if (!user) {
      toast.warning(errStr.login_to_order)
      router.push('/login')
    } else {
      const params = {
        cart_id: cartDetail.id,
        address: data.address,
        payment: { gateway: data.gateway, amount: cartDetail.grand_total, currency: 'GBP' },
        method: data.paymentMethod,
        screen: screen,
      }
      if (data.sms_entry) {
        params['sms_subscribe'] = Number(data.sms_subscribe)
        if (data.sms_subscribe) {
          sessionStorage.setItem(`sms${user.id}`, '1')
        }
      }
      if (data.gateway) {
        params['gateway'] = data.gateway
      }
      postPlaceOrder(params)
    }
  }

  const postPlaceOrder = params => {
    setPaying(true)
    let newCartDetail = null
    axios
      .post(API_URL.checkout, params)
      .then(res => {
        const resultCode = res?.data?.code
        const result = res?.data?.data
        if (resultCode == '0') {
          if (result?.url) {
            router.push(result.url)
            return
          } else {
            router.push('/cart/' + result?.id) // redirect to order complete page
            return
          }
        } else {
          newCartDetail = result
          toast.error(appUtils.getErrorMsgByCode(resultCode, errStr), { toastId: resultCode })
        }
      })
      .catch(e => {
        newCartDetail = e?.response?.data?.data
        handleCartChanged(e)
      })
      .finally(() => {
        if (newCartDetail) {
          setCartDetail(newCartDetail)
          updateCartLocal(newCartDetail)
        }
        setPaying(false)
        mutateCash()
        mutateCredit()
      })
  }

  const handleCartChanged = e => {
    if (e.response && e.response.data) {
      const result = e.response.data.data
      setCartDetail(result)
      updateCartLocal(result)

      if (!appUtils.isEmpty(result) && result.updated) {
        setConfirmModal({ show: true, title: cartStr.inventory_alter, content: appUtils.getInventoryAlert(result.updated, cartStr) })
      } else {
        toast.error(appUtils.handleApiError(e, errStr), { toastId: e.response?.data?.code })
      }
    } else {
      toast.error(appUtils.handleApiError(e, errStr), { toastId: e.response?.data?.code })
    }
  }

  return (
    <>
      <Fragment>
        {hasOrder && cartDetail.p_time && <OrderCountDown expiredAt={cartDetail.p_time} />}
        <form onSubmit={handleSubmit(onSubmit)} className='flex w-full flex-col lg:flex-row'>
          <div className='basis-2/3'>
            <ExpandableBlock
              title={
                <Fragment>
                  <span className='font-semibold text-cyan-v2'>{cartStr.my_basket}</span>
                  <span className='ml-2 mr-auto text-xs text-stone-600'>
                    {'(' + cartDetail.items.length + (cartDetail.items.length > 1 ? ' Items)' : ' Item)')}
                  </span>
                </Fragment>
              }
              openDefault
              innerCls='border-t border-cyan-v2 pt-4'
            >
              {cartDetail.items?.length > 0 ? (
                <CartItems
                  cartDetail={cartDetail}
                  hasOrder={hasOrder}
                  loading={loading}
                  setLoading={setLoading}
                  onChange={res => {
                    setCartDetail(res)
                    updateCartLocal(res)
                  }}
                />
              ) : null}
            </ExpandableBlock>
            {user && (
              <Fragment>
                {hasOrder && cartDetail.billing_address ? (
                  <OrderAddress address={cartDetail.billing_address} />
                ) : (
                  // default open address fields if addrCloseDefault is not true, for instance, is undefined
                  <ExpandableBlock
                    title={cartStr.billing_address}
                    titleTxtCls='text-cyan-v2'
                    openDefault={!getValues('addrCloseDefault')}
                    outerCls='!overflow-visible mt-4'
                    innerCls='grid grid-cols-1 gap-4 border-t border-cyan-v2 pt-4 md:grid-cols-2'
                  >
                    <AddressLayout onChange={onChange} form={formInstance} testidPrefix='addr' />
                  </ExpandableBlock>
                )}
              </Fragment>
            )}
          </div>
          <div className='basis-1/3 lg:ml-4'>
            {cash_back && cash_back != 0 && cartDetail.grand_total > 1 && (
              <div className='my-4 w-full rounded bg-yellow-200 p-4 text-center md:mt-0'>
                {cartStr.place_order_cashback[0]}
                {Format.withCurrency(cartDetail.grand_total * 0.01)}
                {cartStr.place_order_cashback[1]}
              </div>
            )}
            {(!hasOrder || (hasOrder && cartDetail.coupon_code)) && (
              <CouponUsage
                couponData={couponData}
                setCouponData={setCouponData}
                cartDetail={cartDetail}
                setCartDetail={setCartDetail}
                loading={loading || paying}
                setLoading={setLoading}
              />
            )}
            <ExpandableBlock
              title={cartStr.billing_summary}
              titleTxtCls='text-cyan-v2'
              openDefault
              outerCls='sticky z-10 top-20 mt-4'
              innerCls='border-t border-cyan-v2 pt-4'
            >
              {!hasOrder && cash && (Number(cash.amount) > 0 || Number(cash.pending_amount) > 0) && (
                <BalanceUsage
                  type='cash'
                  balance={cash}
                  mutate={mutateCash}
                  cartDetail={cartDetail}
                  setCartDetail={setCartDetail}
                  handleCartChanged={handleCartChanged}
                  loading={loading || paying}
                  setLoading={setLoading}
                />
              )}

              {!hasOrder && credit && (Number(credit.available_amount) > 0 || Number(credit.pending_amount) > 0) && (
                <BalanceUsage
                  type='credit'
                  balance={credit}
                  mutate={mutateCredit}
                  cartDetail={cartDetail}
                  setCartDetail={setCartDetail}
                  handleCartChanged={handleCartChanged}
                  loading={loading || paying}
                  setLoading={setLoading}
                />
              )}

              {/* @ts-ignore */}
              <TotalLayout name={payStr.subtotal} value={cartDetail.subtotal} innerProps={{ 'data-testid': 'txt-basket-subtotal' }} />

              {cartDetail.discount_rules && Array.isArray(cartDetail.discount_rules)
                ? cartDetail.discount_rules.map((item, index) => (
                    <TotalLayout key={index} name={item.name} value={item.amount} isDiscount={true} outerCls='mt-1' />
                  ))
                : null}

              {/* Cash Balance Usage */}
              {Number(cartDetail.use_wallet_amount) > 0 ? (
                <TotalLayout name={walletStr.via_cash} value={cartDetail.use_wallet_amount} isDiscount={true} outerCls='mt-1' />
              ) : null}

              {/* Credit Balance Usage */}
              {Number(cartDetail.use_balance_amount) > 0 && (
                <TotalLayout name={walletStr.via_credit} value={cartDetail.use_balance_amount} isDiscount={true} outerCls='mt-1' />
              )}

              {cartDetail.coupon_code && cartDetail.coupon_name && (
                <TotalLayout
                  name={cartDetail.coupon_name}
                  value={cartDetail.coupon_amount}
                  isDiscount={true}
                  outerCls='mt-1'
                  // @ts-ignore
                  innerProps={{ 'data-testid': 'txt-basket-subtotal' }}
                />
              )}

              <TotalLayout name={payStr.grand_total} value={cartDetail.grand_total} outerCls='mt-2 font-semibold' />

              <Checkbox
                checked={agree}
                onChange={() => setAgree(!agree)}
                extraCls='h-max mt-4 text-sm'
                innerCls='!top-[3px] !translate-y-0'
                text={
                  <span className='ml-2 text-neutral-600'>
                    {cartStr.acknowledge}
                    <Link href='privacy-policy'>
                      <span className='cursor-pointer text-cyan-v2 hover:text-amber-v1'>{cartStr.privacy_and_terms_policy}</span>
                    </Link>
                  </span>
                }
                // @ts-ignore
                span={{ 'data-testid': 'ckb-basket-agreePolicy' }}
              />
              {getValues('sms_entry') ? (
                <Checkbox
                  input={{ ...register('sms_subscribe'), value: 1 }}
                  extraCls='h-max mt-2 mb-4'
                  innerCls='!top-0.5 !translate-y-0'
                  text={<span className='ml-2 text-sm text-neutral-600'>{cartStr.sms_tick}</span>}
                />
              ) : null}

              {!user ? (
                <ButtonWithLoading
                  title={cartStr.checkout}
                  isLoading={loading || paying}
                  className='btn-rd-cyan btn-hover-cyan btn-disabled-grey mt-4 h-10 w-full whitespace-nowrap px-4'
                  onClick={handleSubmit(onSubmit)}
                />
              ) : cartDetail.grand_total > 0 ? (
                <div className='mt-4 flex flex-col items-center gap-4'>
                  {isBarclays ? null : (
                    <GooglePayButton
                      key={'googlePay-rkCart' + cartDetail.id + '-' + cartDetail.grand_total}
                      total={cartDetail.grand_total}
                      form={formInstance}
                      disabled={!agree || loading || paying}
                      setLoading={setPaying}
                      cartId={cartDetail.id}
                      extraCls='btn-rd-cyan btn-hover-cyan btn-disabled-grey h-10 w-full px-4'
                    />
                  )}
                  <ApplePayButton
                    key={'applePay-rkCart' + cartDetail.id + '-' + cartDetail.grand_total}
                    total={cartDetail.grand_total}
                    form={formInstance}
                    disabled={!agree || loading || paying}
                    setDisabled={setPaying}
                    cartId={cartDetail.id}
                    loading={loading}
                    setLoading={setLoading}
                    setCartDetail={setCartDetail}
                    updateCartLocal={updateCartLocal}
                    extraCls='btn-rd-cyan btn-hover-cyan btn-disabled-grey h-10 w-full px-4'
                    style={{ display: 'inherit', width: '100%' }}
                  />
                  <PayButtonGroup
                    disabled={!agree || loading || paying}
                    cardClick={() => {
                      setValue('paymentMethod', 'card', { shouldDirty: true })
                      handleSubmit(onSubmit)()
                    }}
                    paypalClick={() => {
                      setValue('paymentMethod', 'paypal', { shouldDirty: true })
                      handleSubmit(onSubmit)()
                    }}
                    extraCls='h-10 w-full px-4'
                  />
                </div>
              ) : (
                <ButtonWithLoading
                  title={payStr.pay_with_wallet}
                  disabled={!agree || loading || paying}
                  isLoading={loading || paying}
                  className='btn-rd-cyan btn-hover-cyan btn-disabled-grey mt-4 h-10 w-full'
                  onClick={() => {
                    setValue('paymentMethod', 'wallet', { shouldDirty: true })
                    handleSubmit(onSubmit)()
                  }}
                  data-testid='btn-payWallet'
                />
              )}
            </ExpandableBlock>
          </div>
        </form>
      </Fragment>

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        content={confirmModal.content}
        onClose={() => setConfirmModal(prev => ({ ...prev, show: false }))}
        confirmProps={{ onClick: () => setConfirmModal(prev => ({ ...prev, show: false })) }}
        tip={confirmModal.tip}
      />
    </>
  )
}

const CartItems: FC<
  {
    onChange: Action1<CartDetailType>
    hasOrder: boolean
  } & CartContentProps
> = ({ cartDetail, hasOrder, loading, setLoading, onChange }) => {
  const { errors: errStr, cart: cartStr } = useLanguage()
  const router = useRouter()

  const [modalData, setModalData] = useState<{ show: boolean; tempChanged: number; item: Record<string, any> }>({ show: false, tempChanged: 0, item: null })

  const removeItem = (item: Record<string, any>, isPassive?: boolean) => {
    if (hasOrder && !modalData.show) {
      setModalData(prev => ({ ...prev, show: true, item: item }))
      return
    }
    appUtils
      .postData(
        cartDetail.items.length == 1 ? API_URL.clearCart : API_URL.updateCart,
        { cart_id: cartDetail.id, item: { id: item.id, qty: 0 } },
        errStr,
        true,
        setLoading
      )
      .then(res => {
        if (res) {
          /**
           * item removed passively, ie. by code instead of by the user:
           *  1. when enter cart page and competition is already closed
           *  2. when cart item countdown ended
           */
          if (isPassive) {
            toast.warning(cartStr.item_close_remove_success.templated(item.title), { toastId: 'item-removed-' + item.id })
          } else {
            toast.success(cartStr.item_remove_success.templated(item.title), { toastId: 'item-removed-' + item.id })
          }
          onChange(res)
        }
      })
  }

  const updateItem = (item: Record<string, any>, changed: number) => {
    if (hasOrder && !modalData.show) {
      setModalData(prev => ({ ...prev, show: true, tempChanged: changed }))
      return
    }
    appUtils
      .postData(API_URL.updateCart, { cart_id: cartDetail.id, item: { id: item.id, qty: changed } }, errStr, false, setLoading, str => {
        toast.error(str)
        router.reload()
      })
      .then(res => {
        if (res) {
          onChange(res)
          const i = cartDetail.items.findIndex(x => x.id == item.id)
          cartDetail.items[i]['qty'] = changed
          toast.success(cartStr.update_success, { toastId: cartDetail.id })
        } else {
          router.reload()
        }
      })
  }

  return (
    <>
      {cartDetail.items.map((item, index) => (
        <CartItem
          key={`cart-item-${index}-${item.id}`}
          item={item}
          hasOrder={hasOrder}
          isFirst={index == 0}
          loading={loading}
          showCountDown={!(hasOrder && cartDetail.p_time)}
          removeItem={removeItem}
          updateItem={updateItem}
        />
      ))}
      <ConfirmModal
        show={modalData.show}
        onClose={() => setModalData(prev => ({ ...prev, show: false }))}
        title={cartStr.still_want_updated}
        content={cartStr.has_pending_order}
        confirmProps={{
          onClick: () => {
            if (modalData.tempChanged > 0) {
              updateItem(modalData.item, modalData.tempChanged)
              setModalData(prev => ({ ...prev, show: false, tempChanged: 0 }))
            } else {
              removeItem(modalData.item)
              setModalData(prev => ({ ...prev, show: false }))
            }
          },
        }}
      />
    </>
  )
}

const CartItem: FC<{
  item: CartItemType
  hasOrder: boolean
  isFirst: boolean
  loading: boolean
  showCountDown?: boolean
  removeItem: any
  updateItem: any
}> = ({ item, hasOrder, isFirst, loading, showCountDown, removeItem, updateItem }) => {
  const { isDesktop } = useLayout()
  const { errors: errStr, cart: cartStr } = useLanguage()
  const router = useRouter()

  const [qty, setQty] = useState(0)
  useEffect(() => setQty(item.qty), [item.qty])

  const ticketLimit = Math.min(item.user_tickets_left ?? item.user_limit_amount, item.tickets_left)

  function title() {
    return (
      <>
        <span className='cursor-pointer text-sm font-semibold md:text-base' onClick={() => router.push('/competitions/' + item.short_name)}>
          {item.title}
        </span>
        <button onClick={() => removeItem(item)} className={`ml-4 ${hasOrder ? 'hidden' : 'flex'}`} type='button' data-testid='btn-basketItem-delete'>
          <svg className='ml-4 h-3 w-3 cursor-pointer text-error' viewBox='0 0 11 10' fill='currentColor'>
            <path d='M1.57445 9.912L0.926453 9.264L4.86245 5.304L0.926453 1.344L1.57445 0.696L5.51045 4.656L9.42245 0.696L10.0705 1.344L6.13445 5.304L10.0705 9.264L9.42245 9.912L5.51045 5.976L1.57445 9.912Z' />
          </svg>
        </button>
      </>
    )
  }

  return (
    <Fragment>
      <div className={`flex flex-col${isFirst ? '' : ' mt-4 border-t border-cyan-v2 pt-4'}`}>
        <div className='flex justify-between md:hidden'>{title()}</div>
        {!isDesktop && showCountDown ? <CartItemCountDown endDate={item.end_time} onFinish={() => removeItem(item, true)} /> : null}
        <div className='mt-2 flex flex-row md:mt-0'>
          <ImgFallback
            src={item.thumbnail}
            alt={item.title ? item.title : item.thumbnail}
            className='mr-2 max-h-24 w-28 rounded object-contain md:mr-4 md:block md:max-h-36 md:w-48'
          />
          <div className='flex flex-1 flex-col justify-between'>
            <div className='hidden justify-between md:flex'>{title()}</div>
            {isDesktop && showCountDown ? <CartItemCountDown endDate={item.end_time} onFinish={() => removeItem(item, true)} /> : null}
            <div className='flex flex-col text-xs md:text-sm'>
              {item.answer && (
                <>
                  <span className='hidden md:block'>
                    <span className='mr-2 text-neutral-600'>{cartStr.question}:&nbsp;</span>
                    {item.question.endsWith('?') ? item.question : item.question + '?'}
                  </span>
                  <span>
                    <span className='mr-2 text-neutral-600'>{cartStr.chosen_answer}:&nbsp;</span>
                    {item.answer}
                  </span>
                </>
              )}
              <div className='mt-1'>
                <span className='mr-2 text-neutral-600'>{cartStr.price}:&nbsp;</span>
                {Format.withCurrency(item.price)}
                {item.normal_price && <span className='ml-2 text-sm text-error line-through'>{Format.withCurrency(item.normal_price)}</span>}
              </div>
            </div>

            <div className='mt-2 flex w-full items-center text-sm'>
              {['minus', 'plus'].map((svgKey, index) => (
                <Fragment key={'qty-ctrl-' + svgKey}>
                  <IconButton
                    className={`p-1 shadow-lg shadow-stone-200 ${hasOrder ? 'hidden' : 'block'}`}
                    disabled={index == 0 ? qty == 1 || loading : loading || ticketLimit <= qty}
                    onClick={event => {
                      event.preventDefault()
                      if (index == 0) {
                        if (qty > 1) {
                          if (ticketLimit < qty - 1) {
                            // 先加入购物车，再登录的情况，做的修改，
                            toast.warning(errStr.limit_reach)
                          }
                          updateItem(item, Math.min(qty - 1, ticketLimit))
                        }
                      } else {
                        if (ticketLimit <= qty) {
                          return
                        }
                        updateItem(item, Number.parseInt(qty.toString()) + 1)
                      }
                    }}
                    data-testid={svgKey == 'minus' ? 'btn-basketItem-minusOne' : 'btn-basketItem-addOne'}
                  >
                    <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      {svgs[svgKey]}
                    </svg>
                  </IconButton>
                  {index == 0 ? <span className={!hasOrder ? 'px-3 md:px-4' : ''}>{hasOrder ? 'x' + qty : qty}</span> : null}
                </Fragment>
              ))}
              <div className='ml-auto text-right font-semibold text-cyan-v2' data-testid='txt-basketItem-totalPrice'>
                {Format.withCurrency(item.row_total)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  )
}

const BalanceUsage: FC<
  {
    type: 'credit' | 'cash'
    balance: Record<string, any>
    mutate: KeyedMutator<Record<string, any>>
    setCartDetail: Dispatch<SetStateAction<CartDetailType>>
    handleCartChanged: Action1<any>
  } & CartContentProps
> = ({ type, balance, mutate, cartDetail, setCartDetail, handleCartChanged, loading, setLoading }) => {
  const keys =
    type == 'credit'
      ? ['useCreditBalance', 'use_balance', 'balance', 'credit_balance', 'use_balance_amount']
      : ['useCashBalance', 'use_wallet', 'wallet', 'cash_available', 'use_wallet_amount']

  const { errors: errStr, wallet: walletStr } = useLanguage()
  useEffect(() => {
    mutate()
  }, [cartDetail[keys[1]]])
  const onChange = () => {
    if (loading) {
      return
    }
    setLoading(true)
    axios
      .post(API_URL[keys[0]], { cart_id: cartDetail.id, [keys[1]]: !cartDetail[keys[1]] })
      .then(res => {
        const code = res?.data?.code
        if (code == '0') {
          setCartDetail(res.data.data)
          const localMutate = res.data.data?.[keys[2]]
          if (localMutate) {
            mutate(localMutate, { optimisticData: localMutate, revalidate: false, populateCache: false, rollbackOnError: false })
          } else {
            mutate()
          }
        } else {
          toast.error(appUtils.getErrorMsgByCode(code, errStr), { toastId: code })
        }
      })
      .catch(e => handleCartChanged(e))
      .finally(() => setLoading(false))
  }
  const displayAmt = useMemo(
    () =>
      type == 'cash'
        ? floatAddition(balance?.amount ?? 0, cartDetail?.use_wallet_amount ?? 0)
        : floatAddition(balance?.available_amount ?? 0, balance?.pending_amount ?? 0),
    [balance, cartDetail?.use_wallet_amount]
  )
  return (
    <div className='mb-4 flex items-center justify-between text-sm'>
      <span className='mr-2'>{walletStr[keys[3]]}</span>
      <span data-testid={type == 'cash' ? 'txt-bsket-cash' : 'txt-bsket-credit'}>{formatWithGBP(displayAmt)}</span>
      {Number(cartDetail?.[keys[4]]) > 0 && <span className='relative -top-1 ml-1 text-xs text-error'>{-cartDetail[keys[4]]}</span>}
      <Checkbox
        input={{ disabled: loading || displayAmt <= 0 }}
        span={{
          // @ts-ignore
          'data-testid': type == 'cash' ? 'ckb-basket-useCash' : 'ckb-basket-useCredit',
        }}
        checked={cartDetail[keys[1]]}
        onChange={onChange}
        extraCls='!pl-4 ml-auto'
        innerCls='peer-checked:!bg-success peer-checked:hover:!bg-success'
      />
    </div>
  )
}

const CouponUsage: FC<
  {
    couponData: Record<any, any>
    setCouponData: Dispatch<SetStateAction<Record<any, any>>>
    setCartDetail: Dispatch<SetStateAction<CartDetailType>>
  } & CartContentProps
> = ({ couponData, setCouponData, cartDetail, setCartDetail, loading, setLoading }) => {
  const { errors: errStr, cart: cartStr } = useLanguage()
  const applyCoupon = () => {
    if (loading || !couponData.code) {
      return
    }
    setLoading(true)
    appUtils
      .postData(API_URL.addCoupon, { coupon_code: couponData.code, cart_id: cartDetail.id }, errStr, false, setLoading, msg =>
        setCouponData(prev => ({ ...prev, needValid: true, valid: false, message: msg }))
      )
      .then(res => {
        if (res) {
          setCouponData(prev => ({ ...prev, needValid: true, valid: true, message: cartStr.apply_success }))
          setCartDetail(res)
        }
      })
  }
  return (
    <ExpandableBlock title={cartStr.discount_codes} titleTxtCls='text-cyan-v2' openDefault={!!cartDetail.coupon_code} outerCls='mt-4 lg:mt-0'>
      <div className='flex w-full items-center justify-between'>
        <TextInput
          outerCls='flex-1'
          innerCls='!m-0'
          input={{
            'name': 'couponCode',
            'value': couponData.code,
            'placeholder': cartStr.enter_coupon_code,
            'onChange': event => setCouponData({ ...couponData, code: event?.currentTarget?.value || '', needValid: false, message: '' }),
            'disabled': loading || (couponData.needValid && couponData.valid),
            // @ts-ignore
            'data-testid': 'inp-bsket-cpnCode',
          }}
          showValidated={couponData.needValid}
          valid={couponData.valid}
        />
        <ButtonWithLoading
          title={cartStr.apply}
          disabled={loading || !couponData.code || (couponData.needValid && couponData.valid)}
          isLoading={loading}
          className='btn-rd-amber btn-hover-amber btn-disabled-grey ml-4 h-10 px-4'
          onClick={applyCoupon}
          data-testid='btn-bsket-apply'
        />
      </div>
      {couponData.message && (
        <div className={`mt-2 text-xs ${couponData.valid ? 'text-success' : 'text-error'}`} data-testid='txt-cpnApply-text'>
          {couponData.message}
        </div>
      )}
    </ExpandableBlock>
  )
}
